import { Injectable, inject } from '@angular/core';
import { RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { AssetPreloaderService } from '../../core/services/asset-preloader.service';
import { CurriculumService } from '../../core/services/curriculum.service';
import { StudentDataService } from '../../core/services/student-data.service';
import { ThemeParseService } from '../../core/services/theme-parse.service';
import { LoginService } from '../../login/login.service';

export const levelSelectResolver = () => {
  const studentDataService = inject(StudentDataService);
  let nextAssessmentComplete = false;
  let allTasksWereJustCompleted = studentDataService.areAllTasksComplete();
  let isDemoUser = studentDataService.isDemoUser();

  let data = {
    nextAssessmentComplete,
    allTasksWereJustCompleted,
  };

  if (allTasksWereJustCompleted && isDemoUser)
  {
    // Not easy to preload demo user next data so we just return.
    return of(data);
  }
  else
  {
    let obs$: Observable<any> = of({});
    if (allTasksWereJustCompleted)
    {
      obs$ = updateStudentData();
    }

    const assetPreloaderService = inject(AssetPreloaderService);
    //
    // TODO: I think can all be cleaned up a little bit.
    //  More of these tasks can probably also be run in parallel.
    //
    // Resolve request steps:
    //  1. If needed fetch student data, update curriculum, and update the theme properties
    //  2. Load the images
    //  3. Return the resolve data containing flags for nextAssessmentComplete and allTasksWereJustCompleted.
    return obs$.pipe(
      switchMap(() => loadImages(studentDataService, assetPreloaderService)),
      // Return the level select resolver data with the potentially updated nextAssessmentComplete value.
      switchMap(() => of({
          ...data,
          nextAssessmentComplete: studentDataService.getStudentData()?.studentAssessment?.isComplete || false,
        })
      ),
    );
  }
}

const updateStudentData = (): Observable<any> => {
  const curriculumService = inject(CurriculumService);
  const loginService = inject(LoginService);
  const themeService = inject(ThemeParseService);
  const studentDataService = inject(StudentDataService);

  return studentDataService.fetchStudentData().pipe(
    catchError((error) => {
      // Log the error and logout if there is an error fetching student data
      console.log('Error fetching the student data. Logging out...');
      loginService.studentLogout().pipe(take(1)).subscribe();
      throw(error);
    }),
    switchMap(studentData => {
      // Needs to clear the curriculum,
      // otherwise it uses the stored version
      curriculumService.clearCurriculum();
      return curriculumService.loadCurriculum();
    }),
    switchMap(() => themeService.loadThemeProperties()),
  );
}


const loadImages = (studentDataService: StudentDataService, assetPreloaderService: AssetPreloaderService): Observable<any> => {
  let imageList: string[] = [];
  let imageA = studentDataService.getLevelSelectBackgroundA();
  let imageB = studentDataService.getLevelSelectBackgroundB();

  imageList.push(imageA);
  if (imageB)
  {
    imageList.push(imageB);
  }

  return assetPreloaderService.preloadImages(imageList);
}
